import React, { useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import { APP_GTM_ENTRY, CorpSessionInfo, Lodging, RoomInfo } from "redmond";
import { isCorpTenant, getConfigTenant, useIsTravelApp } from "@capone/common";
import clsx from "clsx";
import "./styles.scss";
import {
  AaBusinessLoyaltyPromoBanner,
  ActionLink,
  CloseButtonIcon,
  DesktopPopupModal,
  FlightSummaryPanel,
  HotelSummaryPanel,
  Icon,
  IconName,
  InfoCard,
  MobilePopoverCard,
  useDeviceTypes,
  OfferAppliedBanner,
  TravelAppBanner,
} from "halifax";
import {
  useCheckoutStateSelector,
  ContactSelectors,
  cartFulfillSelectors,
  FlightSelectors,
  WalletSelectors,
} from "@capone/checkout";
import { Product } from "@b2bportal/purchase-api";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  PACKAGE_CONFIRMATION_CODE_TEXT,
  PACKAGE_CONFIRMATION_EARN,
  PACKAGE_CONFIRMATION_HEADING,
  WHATS_NEXT_CARS_DESCRIPTION,
  WHATS_NEXT_CARS_TITLE,
  WHATS_NEXT_HEADING,
} from "./textConstants";
import { PackagesConfirmationConnectorProps } from "./container";
import { LodgingSelectors } from "../../state/selectors";
import { getSliceDetails, postBookingSurveyPageId } from "./utils";
import { useMedalliaBookingData } from "b2b-base/src/components/MedalliaFeedback";
import {
  AVAILABLE,
  EAN_CERTIFICATION,
  getExperimentVariant,
  TREES_MODAL_EXPERIMENT,
  TRAVEL_APP_GTM_EXPERIMENT,
  useExperiments,
  useIsPackagesShoppingApiV2Enabled,
} from "../../../../context/experiments";
import { PackagesPriceBreakdown, PackageTreesCard } from "..";
import { PATH_HOME_SEARCH } from "../../../../utils/paths";
import { getAllSelectedTravelers } from "../../state/selectors/common";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/trackEvent";

type FulfilledPackageProductValue =
  | {
      customerReservationId: string;
    }
  | undefined;

export interface IPackagesConfirmationProps
  extends RouteComponentProps,
    PackagesConfirmationConnectorProps {}

export const PackageConfirmation = ({
  history,
}: IPackagesConfirmationProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { sessionInfo } = React.useContext(ClientContext);
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState(false);

  const expState = useExperiments();
  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT,
  );
  const isTreesExperiment = treesModalExperiment === AVAILABLE;

  const isCorporate = isCorpTenant(getConfigTenant());
  const { isTravelApp } = useIsTravelApp();
  const isEANCertificationExperiment =
    getExperimentVariant(expState.experiments, EAN_CERTIFICATION) === AVAILABLE;

  const isPackagesShoppingApiV2Enabled = useIsPackagesShoppingApiV2Enabled();

  const travelAppGTMExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_APP_GTM_EXPERIMENT,
  );
  const isTravelAppGTMExperiment = travelAppGTMExperiment === AVAILABLE;

  const earnedRewards = useCheckoutStateSelector(
    cartFulfillSelectors.getFulfillEarnedRewards,
  );

  const filteredEarnedRewards = React.useMemo(() => {
    if (!isCorporate) {
      return earnedRewards;
    }

    const isPrimary =
      (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "PR";

    return isPrimary ? earnedRewards : undefined;
  }, [earnedRewards, sessionInfo, isCorporate]);

  const emailAddress = useCheckoutStateSelector(
    ContactSelectors.getEmailAddress,
  );
  const fulfilledProducts = useCheckoutStateSelector(
    cartFulfillSelectors.getFulfilledProducts,
  );

  const selectedLodging = useCheckoutStateSelector(
    LodgingSelectors.getSelectedLodging,
  );
  const fromDate = useCheckoutStateSelector(LodgingSelectors.getHotelFromDate);
  const untilDate = useCheckoutStateSelector(
    LodgingSelectors.getHotelUntilDate,
  );
  const roomInfo = useCheckoutStateSelector(
    LodgingSelectors.getSelectedRoom,
  )?.roomInfo;

  const tripDetails = useCheckoutStateSelector(FlightSelectors.getTripDetails);
  const airports = useCheckoutStateSelector(FlightSelectors.getAirportMap);
  const guests = useCheckoutStateSelector(getAllSelectedTravelers);

  const offerApplied = useCheckoutStateSelector(
    WalletSelectors.getIsWalletOfferApplied,
  );
  const selectedOffer = useCheckoutStateSelector(
    WalletSelectors.getSelectedOffer,
  );

  // Under v2, tripDetails is never populated (the flight was already
  // selected via the GetPackage snapshot, not CTP's own flight shop) — don't
  // treat its absence as "something went wrong" there.
  if (!isPackagesShoppingApiV2Enabled && !tripDetails.slices) {
    history.replace(PATH_HOME_SEARCH);
  }

  const fulfilledPackageProduct = fulfilledProducts?.find(
    (product) => product.type === Product.Package,
  );
  const confirmationCode = (
    fulfilledPackageProduct?.value as FulfilledPackageProductValue
  )?.customerReservationId;

  const outgoingSliceDetails = React.useMemo(
    () => getSliceDetails(true, matchesMobile, airports || {}, tripDetails),
    [matchesMobile, airports, tripDetails],
  );

  const returnSliceDetails = React.useMemo(
    () => getSliceDetails(false, matchesMobile, airports || {}, tripDetails),
    [matchesMobile, airports, tripDetails],
  );

  useMedalliaBookingData(confirmationCode);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {isCorporate && offerApplied && selectedOffer && (
        <OfferAppliedBanner
          offerToApply={selectedOffer}
          isMobile={matchesMobile}
          onViewWalletClick={() => history.push("/travel-offers")}
        />
      )}
      <Box
        className={clsx("package-confirmation-root", { mobile: matchesMobile })}
      >
        <Box className="package-confirmation-header">
          <Box className="check-mark-icon">
            <Icon name={IconName.ConfirmationCheckMark} aria-hidden />
          </Box>
          <Typography
            variant="h2"
            className="package-confirmation-heading-text"
          >
            {PACKAGE_CONFIRMATION_HEADING(isCorporate)}
          </Typography>
        </Box>
        <Box className="package-confirmation-earn-info-container">
          <Typography className="earn-string">
            {PACKAGE_CONFIRMATION_EARN(
              filteredEarnedRewards,
              emailAddress,
              isCorporate,
            )}
          </Typography>
          <Typography className="confirmation-code">
            {PACKAGE_CONFIRMATION_CODE_TEXT(confirmationCode || "")}
          </Typography>
          {isEANCertificationExperiment && (
            <ActionLink
              className={clsx("view-payment-details-cta", "b2b")}
              content={
                <Box className="view-payment-details-cta-content">
                  <Typography
                    variant="body1"
                    className="view-payment-details-cta-text"
                  >
                    View payment details
                  </Typography>
                  <FontAwesomeIcon
                    className="right-chevron"
                    icon={faChevronRight}
                  />
                </Box>
              }
              onClick={() => setOpenPaymentDetailsModal(true)}
            />
          )}
        </Box>

        {isEANCertificationExperiment &&
          (matchesMobile ? (
            <MobilePopoverCard
              className="view-payment-details-mobile-popover"
              open={openPaymentDetailsModal}
              onClose={() => setOpenPaymentDetailsModal(false)}
              topRightButton={
                <CloseButtonIcon
                  onClick={() => setOpenPaymentDetailsModal(false)}
                />
              }
            >
              <PackagesPriceBreakdown />
            </MobilePopoverCard>
          ) : (
            <DesktopPopupModal
              open={openPaymentDetailsModal}
              className="view-payment-details-modal"
              onClose={() => setOpenPaymentDetailsModal(false)}
              invisibleBackdrop={false}
            >
              <PackagesPriceBreakdown />
            </DesktopPopupModal>
          ))}
        {selectedLodging && fromDate && untilDate ? (
          <Box className="hotel-details">
            <HotelSummaryPanel
              selectedLodging={selectedLodging as Lodging}
              checkIn={fromDate}
              checkOut={untilDate}
              isMobile={matchesMobile}
              roomInfo={roomInfo as RoomInfo}
              showRoomCapacityInfo
              adultsCount={guests?.adultCount}
              childrenAges={guests?.children}
            />
          </Box>
        ) : null}

        <Box className="package-flight-details">
          {outgoingSliceDetails ? (
            <FlightSummaryPanel
              {...outgoingSliceDetails}
              isMobile={matchesMobile}
              className="first-slice"
            />
          ) : null}

          {returnSliceDetails ? (
            <FlightSummaryPanel
              {...returnSliceDetails}
              isMobile={matchesMobile}
              className="last-slice"
            />
          ) : null}
        </Box>
        {isCorporate && (
          <AaBusinessLoyaltyPromoBanner
            tripDetails={tripDetails}
            sessionInfo={sessionInfo}
            isMobile={matchesMobile}
            trackEvent={trackEvent}
          />
        )}
        <div id={postBookingSurveyPageId(isCorpTenant(getConfigTenant()))} />
        {isTravelAppGTMExperiment && !isTravelApp && (
          <TravelAppBanner
            variant="confirmation"
            isMobile={matchesMobile}
            onCTAClick={() => {
              trackEvent({
                eventName: APP_GTM_ENTRY,
                properties: {
                  location: "packages_confirmation",
                },
              });
            }}
          />
        )}
        {isTreesExperiment ? <PackageTreesCard isConfirmationScreen /> : null}

        <Box className="what-is-next-section">
          <Typography variant="h6" className="next-header">
            {WHATS_NEXT_HEADING}
          </Typography>
          <Box className="info-cards">
            <InfoCard
              title={WHATS_NEXT_CARS_TITLE}
              subtitle={WHATS_NEXT_CARS_DESCRIPTION}
              onClick={() => {
                history.push("/cars/");
              }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};
